/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  inject,
} from '@angular/core';
import { IsBotService, WINDOW } from '@shared/data-access/services';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { ImageFormat } from '@shared/media/data-access';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { isPlatformBrowser } from '@angular/common';

const IMAGE_HOST = 'https://dks.scene7.com/is/image/';

@Component({
  selector: 'homefield-product-image',
  templateUrl: './product-image.component.html',
  styleUrl: './product-image.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  // Disabling this error because the idea is for us in the future to use the hmf-product-card
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class HomefieldProductImageComponent implements OnChanges, OnInit {
  private readonly document: Document = inject(DOCUMENT);
  protected platformId: object = inject(PLATFORM_ID);
  protected window: Window = inject(WINDOW);
  private readonly analyticsService: ProductAnalyticsService = inject(ProductAnalyticsService);
  private readonly isBotService: IsBotService = inject(IsBotService);
  private readonly renderer: Renderer2 = inject(Renderer2);

  @Input() defaultColor: string | null = null;
  @Input() ecode = '';
  @Input() height = '200';
  @Input() imageUrl = '';
  @Input() imgClasses = '';
  @Input() imgFormat: ImageFormat = ImageFormat.JPEG;
  @Input() isLCPImage = false;
  @Input() isNgOptimizedImageSupported = false;
  @Input() productName = '';
  @Input() selectedSwatchColor?: string;
  @Input() width = '100';

  @Output() readonly errorHandlerFunc = new EventEmitter<void>();

  private imageColor = '';
  // Added the # placeholder because the NgOptimizeImage trigger the test error of the
  // ngSrc initialized with "falsy" value
  public imageSrc = '#';

  private static isLCPLoaded = false;

  ngOnChanges() {
    this.imageColor = this.selectedSwatchColor || this.defaultColor || this.imageColor;
    this.imageColor = this.imageColor?.replace(/[^\w]/gi, '_');

    let imagePath = IMAGE_HOST;
    if (
      isPlatformBrowser(this.platformId) &&
      window.location.hostname.includes(`dksxchange`) &&
      !window.location.hostname.includes(`prod-`)
    ) {
      imagePath = `${imagePath}GolfGalaxyTest`;
    } else {
      imagePath = `${imagePath}dkscdn`;
    }

    if (this.imageUrl) {
      this.imageSrc = this.imageColor
        ? this.imageUrl.replace(/_([^?]+)/, `_${this.imageColor}`)
        : this.imageUrl;
    } else {
      this.imageSrc = this.imageColor
        ? `${imagePath}/${this.ecode}_${this.imageColor}_is/?wid=252&hei=252&qlt=85,0&fmt=jpg&op_sharpen=1`
        : `${imagePath}/${this.ecode}_is/?wid=252&hei=252&qlt=85,0&fmt=jpg&op_sharpen=1`;
    }
  }

  ngOnInit(): void {
    if (!HomefieldProductImageComponent.isLCPLoaded) {
      this.preloadImage(this.imageSrc);
    }
  }

  /**
   * @description Posts to rumrunner when a missing product image is detected
   */
  public handleImageError(requestedImg: string): void {
    if (
      isPlatformBrowser(this.platformId) &&
      !this.isBotService.isBot(window.navigator.userAgent)
    ) {
      this.analyticsService.emitMissingImageEvent({
        pageUrl: window.location.href,
        requestedImg,
        ecode: this.ecode,
        productName: this.productName,
        color: this.imageColor,
      });

      this.imageSrc = DCSG_CONSTANTS.imageUnavailableResized;
    }

    if (this.errorHandlerFunc) {
      this.errorHandlerFunc.emit();
    }
  }

  /**
   * @description Creates a native DOM link element and sets necessary properties
   *
   * @param imageUrl image URL
   */
  private preloadImage(imageUrl: string): void {
    const linkEl = this.renderer.createElement('link');
    this.renderer.setAttribute(linkEl, 'rel', 'preload');
    this.renderer.setAttribute(linkEl, 'as', 'image');
    this.renderer.setAttribute(linkEl, 'fetchpriority', 'high');
    this.renderer.setAttribute(linkEl, 'href', imageUrl);

    const headEl = this.document.head;
    this.renderer.insertBefore(headEl, linkEl, headEl.firstChild);

    HomefieldProductImageComponent.isLCPLoaded = true;
  }
}
